import axios, { AxiosInstance, AxiosError } from 'axios';
import type { PaginationParams } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/admin/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/admin/auth/logout');
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/admin/auth/me');
    return response.data;
  },
};

// Public API
export const publicAPI = {
  getProfile: async (uniqueUrl: string) => {
    const response = await api.get(`/public/card/${uniqueUrl}`);
    return response.data;
  },

  downloadVCard: async (uniqueUrl: string) => {
    const response = await api.get(`/public/vcard/${uniqueUrl}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  trackEvent: async (employeeId: string, eventType: string, referrer?: string) => {
    const response = await api.post('/public/track', {
      employeeId,
      eventType,
      referrer,
    });
    return response.data;
  },
};

// Employee API
export const employeeAPI = {
  getAll: async (params?: PaginationParams) => {
    const response = await api.get('/admin/employees', { params });
    return response.data;
  },

  getOne: async (id: string) => {
    const response = await api.get(`/admin/employees/${id}`);
    return response.data;
  },

  create: async (formData: FormData) => {
    const response = await api.post('/admin/employees', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, formData: FormData) => {
    const response = await api.put(`/admin/employees/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/admin/employees/${id}`);
    return response.data;
  },

  toggleStatus: async (id: string) => {
    const response = await api.patch(`/admin/employees/${id}/toggle-status`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },

  getAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
    employeeId?: string;
  }) => {
    const response = await api.get('/admin/dashboard/analytics', { params });
    return response.data;
  },

  exportAnalytics: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await api.get('/admin/dashboard/analytics/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  get: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  update: async (formData: FormData) => {
    const response = await api.put('/admin/settings', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default api;
